import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { projectPartnership } from '@affily/shared/projection';
import { formatCents, formatCount, formatEpc } from '@affily/shared/money';
import { Field, Input, Unknown } from './ui.jsx';

/**
 * The calculator embedded on a public program page.
 *
 * Prefilled with the program's REAL terms and, where we have it, its real
 * tracked conversion rate — so a creator is projecting against measured
 * behaviour rather than a guess. Both sides of the projection are shown
 * together, because there is nothing here a brand and a creator should be told
 * differently.
 */
export function InlineCalculator({ program, className = '' }) {
  const trackedCvr = program.performance?.conversionRatePct;

  const [inputs, setInputs] = useState({
    clicks: '1000',
    // Default to what this program actually converts at when we know it.
    conversionRatePct: trackedCvr != null ? String(Number(trackedCvr.toFixed(2))) : '2',
    cycleRetentionPct: '90',
  });

  const set = (key) => (e) => setInputs((f) => ({ ...f, [key]: e.target.value }));

  const terms = useMemo(
    () => ({
      commissionType: program.commissionType,
      rateBps: program.rateBps,
      flatAmountCents: program.flatAmountCents,
      cookieWindowDays: program.cookieWindowDays,
      recurringMode: program.recurringMode ?? 'one_time',
      recurringMonths: program.recurringMonths ?? null,
    }),
    [program],
  );

  const projection = useMemo(() => {
    try {
      return projectPartnership(terms, {
        clicks: Number(inputs.clicks) || 0,
        conversionRatePct: Number(inputs.conversionRatePct) || 0,
        orderAmountCents: program.averageOrderValueCents ?? 5000,
        cycleRetentionPct: Number(inputs.cycleRetentionPct) || 100,
      });
    } catch {
      return null;
    }
  }, [terms, inputs, program.averageOrderValueCents]);

  const recurring = terms.recurringMode === 'fixed_period';

  return (
    <section className={className} aria-labelledby="calc-heading">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <h2
          id="calc-heading"
          className="text-2xs font-semibold uppercase tracking-wider text-ink-faint"
        >
          What this would pay you
        </h2>
        <p className="text-xs text-ink-faint">
          {trackedCvr != null
            ? 'Prefilled with this program’s real tracked conversion rate'
            : 'No tracked conversions yet — the rate below is your assumption'}
        </p>
      </div>

      <div className="rounded-lg border border-rule bg-paper-raised p-4">
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Clicks you'd send">
            <Input type="number" min="0" value={inputs.clicks} onChange={set('clicks')} />
          </Field>
          <Field
            label="Conversion rate (%)"
            hint={trackedCvr != null ? 'This program’s measured rate' : 'Your estimate'}
          >
            <Input
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={inputs.conversionRatePct}
              onChange={set('conversionRatePct')}
            />
          </Field>
          {recurring && (
            <Field label="Monthly retention (%)" hint="How many customers stay">
              <Input
                type="number"
                min="0"
                max="100"
                value={inputs.cycleRetentionPct}
                onChange={set('cycleRetentionPct')}
              />
            </Field>
          )}
        </div>

        {projection && (
          <>
            <div className="mt-4 grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-rule bg-rule sm:grid-cols-4">
              <Cell label="Conversions" value={formatCount(Math.floor(projection.conversions))} />
              <Cell
                label="EPC"
                value={projection.epc === null ? null : formatEpc(projection.epc, { currency: program.currency })}
                empty="No clicks, so nothing to divide by"
              />
              <Cell
                label="Commission"
                value={formatCents(projection.grossCents, { currency: program.currency })}
                hint="what the brand pays, before Affily’s fee"
                emphasis
              />
            </div>

            {recurring && (
              <p className="mt-2 text-xs text-ink-faint">
                One customer is worth {formatCents(projection.perConversion.lifetimeNetCents, { currency: program.currency })} to
                you across all {projection.contractedCycles} cycles, or about{' '}
                {formatCents(
                  Math.round(projection.perConversion.netAmountCents * projection.effectiveCycles),
                  { currency: program.currency },
                )}{' '}
                at {inputs.cycleRetentionPct}% monthly retention.
              </p>
            )}

            <p className="mt-2 text-xs text-ink-faint">
              A projection from your assumptions, not a promise — but computed by the same function
              that pays the commission. Affily’s fee is shown in your account once you sign up.{' '}
              <Link
                to="/tools/affiliate-earnings-calculator"
                className="text-accent hover:underline"
              >
                Full calculator
              </Link>
            </p>
          </>
        )}
      </div>
    </section>
  );
}

function Cell({ label, value, hint, empty, emphasis = false }) {
  return (
    <div className="bg-paper-raised px-4 py-3">
      <p className="text-2xs font-semibold uppercase tracking-wider text-ink-faint">{label}</p>
      <p
        className={`mt-1 tabular-nums ${
          emphasis ? 'text-xl font-semibold text-accent' : 'text-lg font-medium text-ink'
        }`}
      >
        {value ?? <Unknown reason={empty} />}
      </p>
      {hint && <p className="text-xs text-ink-faint">{hint}</p>}
    </div>
  );
}
